import { useMemo } from "react";
import {
  find,
  flatMap,
  get,
  head,
  isFunction,
  isNumber,
  last,
  map,
  max,
  noop,
  reduce,
  uniq,
} from "lodash";
import { content } from "@glossy/theme";
import { GlossyTheme } from "@glossy/theme/types";
import { getAllDemographicRanges } from "@linkfluence/models/demographic-range";
import { Gender, getGenderColor } from "@linkfluence/models/gender";
import { Locale } from "@linkfluence/models/locale";
import { division, percent } from "@tootools/number";
import { tr } from "@radarly/i18n";
import { RangeData, Serie } from "./model";

export const callRangeValue = (callback, bar) => () =>
  isFunction(callback) && callback(bar.range, bar.serieId);

export const useGetSeries = (locale: Locale) =>
  useMemo(
    () => [
      {
        color: getGenderColor(Gender.Female),
        id: Gender.Female as const,
        label: tr(locale, "FACETS.DEMOGRAPHY.VALUES.F"),
      },
      {
        color: getGenderColor(Gender.Male),
        id: Gender.Male as const,
        label: tr(locale, "FACETS.DEMOGRAPHY.VALUES.M"),
      },
    ],
    [locale]
  );

export const getRanges = (locale: Locale) =>
  map(getAllDemographicRanges(), (range) => ({
    id: range,
    label: tr(locale, "FACETS.DEMOGRAPHY.VALUES." + range.replace("-", "_")),
  }));

export const callRange = (callback, range) =>
  isFunction(callback) ? () => callback(range) : noop;

const getTotals = (fields, data) =>
  reduce(
    fields,
    (acc, field) => {
      acc[field] = getTotal(data, field);
      return acc;
    },
    {}
  );

export const getTotal = (data, field) =>
  reduce(
    data,
    (acc, entry) =>
      reduce(
        entry.data,
        (acc, value) =>
          acc + (isNumber(value) ? value : get(value, [field], 0)),
        acc
      ),
    0
  );

const getTooltip = (data) => ({
  count: get(head(data), "count", 0),
  color: get(head(data), "color"),
  label: get(head(data), "label"),
});

const getBiggestPercentFromField = (data, total, field) =>
  reduce(
    data,
    (acc, entry) =>
      reduce(
        entry.data,
        (acc, value) =>
          Math.max(
            percent(isNumber(value) ? value : get(value, [field], 0), total),
            acc
          ),
        acc
      ),
    0
  );

const getBiggestPercent = (data, totals) =>
  max(
    map(totals, (total, key) => getBiggestPercentFromField(data, total, key))
  );

const getFields = (series) =>
  uniq(
    flatMap(
      map(series, (serie) =>
        serie.labels ? map(serie.labels, "id") : serie.id
      )
    )
  );

const getCount = (range, id, field) =>
  get(range, id !== field ? ["data", id, field] : ["data", id], 0);

const getSubSerie = (id, field, serie) =>
  id !== field && serie.labels ? find(serie.labels, { id: field }) : serie;

const getDataBars = (fields, serie, data, range, id, theme) => {
  const totals = getTotals(fields, data);
  const ratio = division(100, getBiggestPercent(data, totals)) || 1;
  return reduce(
    fields,
    (acc, field) => {
      if (id === field || serie.labels) {
        const count = getCount(range, id, field);
        const pct = percent(count, totals[field] || 0);
        const subSerie = getSubSerie(id, field, serie);
        acc.push({
          count,
          percent: pct,
          height: pct * Number(ratio),
          id: field,
          color: subSerie.color,
          fadedColor: content({ theme }, "tertiary"),
          label: subSerie.label,
          dashed: subSerie.type === "dashed",
        });
      }
      return acc;
    },
    []
  );
};

const getBars = (
  series: Serie[],
  ranges,
  data: RangeData[],
  theme: GlossyTheme
) => {
  const fields = getFields(series);
  return map(series, ({ id, ...serie }) =>
    map(ranges, ({ id: range }) => {
      const foundRange = find(data, { range });
      const dataBars = getDataBars(fields, serie, data, foundRange, id, theme);
      return {
        id: range + "_" + id,
        range: range,
        serieId: id,
        data: dataBars,
        tooltip: getTooltip(dataBars),
      };
    })
  );
};

export const useBars = (
  series: Serie[],
  ranges,
  data: RangeData[],
  theme: GlossyTheme
) =>
  useMemo(() => {
    const bars = getBars(series, ranges, data, theme);
    const topBars = head(bars);
    const bottomBars = last(bars);
    return [topBars, bottomBars] as const;
  }, [series, ranges, data, theme]);
